// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  // For non-transit content types (e.g. application/json) we pass the
  // body through unchanged — the caller is expected to have already
  // stringified it.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
/**
 * Active homepage banners, in the order the admin arranged them.
 *
 * Banners are draft listings, which the public Marketplace API cannot see, so
 * this goes through our own server (server/api/banners.js).
 *
 * Written against the global `fetch` rather than the shared `request` helper
 * above, because the landing page loads its data during server rendering too
 * and `request` reaches for `window.fetch`. On the server there is no origin
 * to infer, so the caller passes `marketplaceRootURL` from the config.
 *
 * Never rejects: a homepage that renders without its banners beats a homepage
 * that does not render.
 *
 * @param {string} [marketplaceRootURL] - required during server rendering
 * @param {string} [placement] - 'partner' for the Partner page's banners;
 *   anything else is the homepage
 * @returns {Promise<{banners: Array}>}
 */
export const getBanners = (marketplaceRootURL = null, placement = null) => {
  const base = typeof window !== 'undefined' ? apiBaseUrl() : apiBaseUrl(marketplaceRootURL);
  if (typeof window === 'undefined' && !marketplaceRootURL) {
    return Promise.resolve({ banners: [] });
  }
  const query = placement === 'partner' ? '?placement=partner' : '';
  return fetch(`${base}/api/banners${query}`, { headers: { Accept: 'application/json' } })
    .then(res => (res.ok ? res.json() : { banners: [] }))
    .catch(() => ({ banners: [] }));
};

/**
 * The verified Zuprize Partners, `{ partners: [{ id, displayName, username }] }`
 * (server/api/partners.js). Used during server rendering too, so like
 * getBanners it uses the global fetch and the configured root URL. Never
 * rejects: a failure is no partners, and the Partner page then 404s.
 *
 * @param {string} [marketplaceRootURL] - required during server rendering
 * @returns {Promise<{partners: Array}>}
 */
export const getPartners = (marketplaceRootURL = null) => {
  const onServer = typeof window === 'undefined';
  if (onServer && !marketplaceRootURL) {
    return Promise.resolve({ partners: [] });
  }
  const base = onServer ? apiBaseUrl(marketplaceRootURL) : apiBaseUrl();
  return fetch(`${base}/api/partners`, { headers: { Accept: 'application/json' } })
    .then(res => (res.ok ? res.json() : { partners: [] }))
    .catch(() => ({ partners: [] }));
};

/**
 * Tells the server a banner changed, so the homepage stops serving its cached
 * copy. Fire-and-forget: the banner is already saved by the time this runs,
 * and a failed cache hint must never look like a failed save. Worst case the
 * cache expires on its own a minute later.
 */
export const invalidateBanners = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  return fetch(`${apiBaseUrl()}/api/banners/invalidate`, { method: 'POST' }).catch(() => {});
};

export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Listing AI — runs Claude vision over uploaded photos and returns
// suggested title / description / publicData enum picks for the Edit
// Listing flow. The endpoint speaks plain JSON (no Sharetribe SDK
// transit), so we bypass the default transit serialiser.
//
// See `server/api/analyse-listing.js` for the expected body shape and
// response.
export const analyseListing = (body, options = {}) => {
  return post('/api/analyse-listing', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
};

// Posten pickup-point lookup. Server calls Bring with the buyer's
// postcode (and street if provided) and returns the nearest ~5
// points, normalised to the shape PickupPointPicker renders. Only
// `posten-stor` returns a list; `posten-liten` returns
// `{ pickupPoints: null }` so the UI hides the picker.
//
// See `server/api/pickup-points.js` for the body shape + response.
export const getPickupPoints = body => {
  return post('/api/pickup-points', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Posten/Bring booking. Called by the client after `confirm-payment`
// succeeds; the server writes the result to
// `transaction.metadata.shipping` via the Integration API and returns
// the same record. Plain JSON in/out.
//
// Body: { transactionId: string, pickupDate?: 'YYYY-MM-DD' }
// Response: { shipping: <metadata.shipping shape> }
//
// `pickupDate` opt-in is the Norgespakke Liten mailbox-pickup variant:
// when supplied, the seller is asking Posten to come collect from
// their own postkasse on that date instead of dropping off at PiB.
// Ignored for Stor (Bring doesn't support pickup-from-sender on the
// pickup-point product).
//
// See `server/api/shipping/book-shipment.js`.
export const bookShipment = body => {
  return post('/api/book-shipment', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Cancel the Bring mailbox pickup (henting) for a transaction whose
// order is being cancelled. Server no-ops for everything except
// Norgespakke Liten with a booked pickup. Best-effort: never throws in
// a way that should block the cancel — the response just reports
// whether a pickup was cancelled.
//
// Body: { transactionId: string }
// Response: { canceled: boolean, reason?: string }
//
// See `server/api/shipping/cancel-shipment.js`.
export const cancelShipment = body => {
  return post('/api/cancel-shipment', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Reschedule a booked Norgespakke Liten mailbox pickup to a new date.
// Keeps the same consignment + sendekode. Surfaces Bring's rejection
// (too soon / already picked up / same day) so the seller can pick
// another date.
//
// Body: { transactionId: string, pickupDate: 'YYYY-MM-DD' }
// Response: { shipping: <updated metadata.shipping> }
//
// See `server/api/shipping/modify-pickup.js`.
export const modifyShipmentPickup = body => {
  return post('/api/modify-pickup', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Available mailbox-pickup dates for the Norgespakke Liten henting
// flow. Server proxies Bring's Shipping Guide endpoint; we never hold
// Mybring credentials on the client. Sundays/holidays + today/past are
// excluded by Bring; the response window caps at 30 days into the
// future per their docs.
//
// Body: { postcode: '0185', street?: 'Storgata 5' }
// Response: { pickupDates: Array<{ date: 'YYYY-MM-DD', latestBookingTime?: 'HH:MM' }> }
//
// See `server/api/shipping/pickup-dates.js`.
export const getShippingPickupDates = body => {
  return post('/api/shipping/pickup-dates', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Seller's Stripe Connect account ID for a listing. Needed by the
// checkout's Apple Pay flow so `stripe.elements({ onBehalfOf })` lines
// up with the `transfer_data.destination` Sharetribe sets on the
// PaymentIntent. Without it, Stripe rejects the Express Checkout
// Element confirm with an on_behalf_of mismatch.
//
// Body: { listingId: string }
// Response: { stripeAccountId: string | null }
//
// See `server/api/seller-stripe-account.js`.
export const getSellerStripeAccount = body => {
  return post('/api/seller-stripe-account', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Vanity username helpers.
// checkUsername: { username } -> { available, reason }
export const checkUsername = body => {
  return post('/api/users/check-username', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// getUserByUsername: { username } -> { userId }
export const getUserByUsername = body => {
  return post('/api/users/get-by-username', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Newsletter (Mailchimp). Both endpoints read the authenticated session
// user server-side, so no body is needed.
//
// subscribeToNewsletter: subscribes the current user to the ZUPRIZE
// audience — but only if they opted in (privateData.acceptsMarketing).
//
// - Default (signup): fire-and-forget; the server responds immediately and
//   retries the Mailchimp call in the background. -> { subscribed: true }
// - { wait: true } (profile settings): the server awaits Mailchimp and
//   rejects (502) if the subscribe fails, so the caller can revert the
//   checkbox and show an error.
export const subscribeToNewsletter = (opts = {}) => {
  const body = { wait: !!opts.wait, ...(opts.source ? { source: opts.source } : {}) };
  return post('/api/mailchimp/subscribe', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// unsubscribeFromNewsletter: flips the current user's consent off and sets
// their Mailchimp status to unsubscribed. Backs the "Meld av nyhetsbrev"
// button. -> { unsubscribed }
export const unsubscribeFromNewsletter = () => {
  return post('/api/mailchimp/unsubscribe', JSON.stringify({}), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Listing review (admin panel at /admin/listings). The server checks the
// caller's admin role on every call; see `server/api/admin/listing-review.js`.
//
// getPendingListings: -> transit { data: [listing], included: [user, image] },
// newest first, without listings that are waiting on the seller's changes.
export const getPendingListings = () => {
  return request('/api/admin/pending-listings', { method: methods.GET });
};

// The admin overview at /admin/oversikt. One call, every section: the server
// aggregates it and holds the result for a few minutes, because it is several
// paginated Integration API queries and the page is a glance, not a feed.
// See `server/api/admin/overview.js`.
//
// getAdminOverview: -> { generatedAt, listings, brands, stripe, noListing, mostSaved, users }
export const getAdminOverview = () => {
  return request('/api/admin/overview', {
    method: methods.GET,
    headers: { Accept: 'application/json' },
  });
};

// getPendingListingsCount: -> { count }
export const getPendingListingsCount = () => {
  return request('/api/admin/pending-listings/count', {
    method: methods.GET,
    headers: { Accept: 'application/json' },
  });
};

// approveListing: listingId (uuid string) -> { approved }
export const approveListing = listingId => {
  return post(`/api/admin/listings/${listingId}/approve`, JSON.stringify({}), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// getPartnerListings: -> transit { data, included }, every published listing
// of every verified partner (the Butikker tab). Admin only.
export const getPartnerListings = () => {
  return request('/api/admin/partner-listings', { method: methods.GET });
};

// setListingFeatured: listingId, featured (boolean) -> { featured }
// Puts a partner's listing in or out of the Butikker page's carousel. Admin only.
export const setListingFeatured = (listingId, featured) => {
  return post(`/api/admin/listings/${listingId}/featured`, JSON.stringify({ featured }), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// createBusinessInvite: { email? } -> { url, email, expiresAt, validForDays }
// Admin only. The link admits the business user type on the signup page.
export const createBusinessInvite = body => {
  return post('/api/admin/business-invites', JSON.stringify(body || {}), {
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * verifyBusinessInvite: token -> { valid, userType?, email?, expiresAt? }
 *
 * Asked from the signup page's loadData, which runs during server rendering
 * too, so like getBanners this uses the global `fetch` and takes the root URL
 * from the config when there is no window to infer it from. Anything that
 * goes wrong verifies nothing: the visitor gets a 404, not a business form.
 *
 * @param {string} token
 * @param {string} [marketplaceRootURL] - required during server rendering
 */
export const verifyBusinessInvite = (token, marketplaceRootURL = null) => {
  const onServer = typeof window === 'undefined';
  if (onServer && !marketplaceRootURL) {
    return Promise.resolve({ valid: false });
  }
  const base = onServer ? apiBaseUrl(marketplaceRootURL) : apiBaseUrl();
  return fetch(`${base}/api/business-invites/verify?token=${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  })
    .then(res => (res.ok ? res.json() : { valid: false }))
    .catch(() => ({ valid: false }));
};

// rejectListing: listingId, { reason, personalMessage } -> { rejected, emailSent }
export const rejectListing = (listingId, body) => {
  return post(`/api/admin/listings/${listingId}/reject`, JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// deleteListing: the seller's "Slett annonse". listingId, reason, reasonText? -> { deletedAt, state }
// Refused with { error: 'active-order' } while an order on it is unfinished.
// See `server/api/delete-listing.js`.
export const deleteListing = (listingId, reason, reasonText) => {
  return post(`/api/listings/${listingId}/delete`, JSON.stringify({ reason, reasonText }), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// resubmitListing: the seller's "Send inn på nytt". listingId -> { resubmittedAt }
// See `server/api/resubmit-listing.js`.
export const resubmitListing = listingId => {
  return post(`/api/listings/${listingId}/resubmit`, JSON.stringify({}), {
    headers: { 'Content-Type': 'application/json' },
  });
};
